import {
  applyDocumentTheme,
  type ColorSchemeSource,
  mediaColorSchemeSource,
  type PreferredTheme,
  resolveThemePreference,
  type ThemePreference,
  type ThemePreferenceStore,
} from '@words-happen/theme-preference';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { WordsHappenTheme } from './words-happen-theme';

export interface ThemePreferenceContextValue {
  /** What the player chose. */
  preference: ThemePreference;
  /** What it shows now: for `auto`, whichever matches the device. */
  theme: PreferredTheme;
  /** Saves `preference` and shows it at once. */
  setPreference: (preference: ThemePreference) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | undefined>(undefined);

/** The player's theme preference, from the nearest `WordsHappenThemeProvider`. */
export const useThemePreference = (): ThemePreferenceContextValue => {
  const value = useContext(ThemePreferenceContext);
  if (!value) {
    throw new Error('useThemePreference needs a WordsHappenThemeProvider above it');
  }
  return value;
};

const deviceColorScheme = mediaColorSchemeSource();

export interface WordsHappenThemeProviderProps {
  /** Where the preference is kept: `documentThemePreferenceStore(SESSION_COOKIE_DOMAIN)` in the apps. */
  store: ThemePreferenceStore;
  /** The device's light or dark setting, which `auto` follows. Defaults to `prefers-color-scheme`. */
  colorScheme?: ColorSchemeSource;
  /** Keeps `data-wh-theme` on `<html>` in step, as the boot script set it, so the canvas matches. Off in showcases. */
  syncDocument?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The app root's theme: resolves the player's preference to Meadow or Noir and renders `WordsHappenTheme` with it.
 * `auto` follows the device's setting live. The first render already uses the saved preference.
 */
export const WordsHappenThemeProvider: React.FC<WordsHappenThemeProviderProps> = ({
  store,
  colorScheme = deviceColorScheme,
  syncDocument = true,
  className,
  children,
}) => {
  const [preference, setPreferenceState] = useState(store.read);
  const prefersDark = useSyncExternalStore(colorScheme.subscribe, colorScheme.prefersDark, () => false);
  const theme = resolveThemePreference(preference, prefersDark);

  useLayoutEffect(() => {
    if (syncDocument) {
      applyDocumentTheme(theme);
    }
  }, [syncDocument, theme]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      store.write(next);
      setPreferenceState(next);
    },
    [store],
  );

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference]);

  return (
    <ThemePreferenceContext.Provider value={value}>
      <WordsHappenTheme theme={theme} className={className}>
        {children}
      </WordsHappenTheme>
    </ThemePreferenceContext.Provider>
  );
};
